import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Aggregates "Frequently bought together" recommendations across every item
 * currently in the cart. Calls `public.get_frequently_bought_together` once
 * per cart product (carts are small in practice) and sums the `co_count`
 * scores so products that pair with multiple cart items float to the top.
 *
 * The returned UUIDs are filtered to exclude anything already in the cart so
 * the cross-sell never suggests a duplicate.
 */
export interface CartCrossSellRow {
  product_id: string;
  score: number;
}

export function useCartCrossSell(cartDbIds: string[], limit = 4) {
  const [rows, setRows] = useState<CartCrossSellRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Stabilize the dep so identity changes don't refetch on every render.
  const key = cartDbIds.slice().sort().join(",");

  useEffect(() => {
    const ids = cartDbIds.filter(Boolean);
    if (ids.length === 0) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const results = await Promise.all(
        ids.map((pid) =>
          supabase.rpc("get_frequently_bought_together", {
            _product_id: pid,
            _limit: 8,
            _min_co_count: 1,
          })
        )
      );
      if (cancelled) return;
      const inCart = new Set(ids);
      const scores = new Map<string, number>();
      for (const res of results) {
        const data = Array.isArray(res?.data) ? res.data : [];
        for (const r of data as any[]) {
          const pid = String(r.product_id);
          if (inCart.has(pid)) continue;
          scores.set(pid, (scores.get(pid) || 0) + (Number(r.co_count) || 0));
        }
      }
      const sorted = Array.from(scores.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, Math.max(limit, 1))
        .map(([product_id, score]) => ({ product_id, score }));
      setLoading(false);
      setRows(sorted);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, limit]);

  return { rows, loading };
}